// src/public/PublicSalesAssessment.js
import { useState, useEffect } from 'react';
import {
  Box, Container, Paper, TextField, Button, Typography,
  CircularProgress, Alert, InputAdornment, List, ListItem,
  ListItemIcon, ListItemText
} from '@mui/material';
import {
  Email as EmailIcon,
  Timer as TimerIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import PublicSalesAssessmentQuestions from './PublicSalesAssessmentQuestions';

const TEST_TIME_LIMIT = 20; // minutos

const PublicSalesAssessment = () => {
  const { testId } = useParams();
  const { t } = useTranslation();
  // Email pre-llenado por query param (?email=...), usado por el portal del
  // candidato para no pedirle de nuevo un dato que ya conocemos de su sesión
  const [searchParams] = useSearchParams();

  const [currentStep, setCurrentStep] = useState('email');
  const [applicantEmail, setApplicantEmail] = useState((searchParams.get('email') || '').trim());
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [testData, setTestData] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(TEST_TIME_LIMIT * 60);
  const [completionInfo, setCompletionInfo] = useState({ timeExpired: false, submitFailed: false });

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleEmailChange = (event) => {
    const value = event.target.value;
    setApplicantEmail(value);
    if (!value.trim()) return setEmailError('');
    setEmailError(isValidEmail(value) ? '' : t('salesAssessment.public.invalidEmail'));
  };

  const handleAccessValidation = async (event) => {
    event?.preventDefault?.();

    if (!applicantEmail.trim()) {
      setEmailError(t('salesAssessment.public.enterEmail'));
      return;
    }

    if (!isValidEmail(applicantEmail)) {
      setEmailError(t('salesAssessment.public.invalidEmail'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.get(
        `/sales-assessments-public/${testId}?email=${encodeURIComponent(applicantEmail)}`
      );

      if (response.data.errorCode === 0) {
        const data = response.data.response?.data;
        setTestData(data);

        // Determinar el paso según el estado del test
        if (data.status === 'sent' || data.status === 'pending') {
          setCurrentStep('instructions');
        } else if (data.status === 'started') {
          // Calcular tiempo restante desde startedAt
          const startedSeconds = data.startedAt?._seconds ?? data.startedAt?.seconds ?? 0;
          const startedTime = startedSeconds * 1000;
          const elapsed = Math.floor((Date.now() - startedTime) / 1000);
          const remaining = (TEST_TIME_LIMIT * 60) - elapsed;

          setTimeRemaining(remaining > 0 ? remaining : 0);
          setCurrentStep('test');
        } else if (['completed', 'processing', 'evaluated'].includes(data.status)) {
          setCurrentStep('completed');
        } else if (data.status === 'expired') {
          setCurrentStep('expired');
        } else {
          setError(t('salesAssessment.public.invalidTestStatus'));
          setCurrentStep('error');
        }
      } else {
        setError(response.data.errorMessage || t('salesAssessment.public.emailValidationError'));
        setCurrentStep('error');
      }
    } catch (err) {
      console.error('Error validating email:', err);
      const statusCode = err.response?.status;

      if (statusCode === 404) {
        setError(t('salesAssessment.public.testNotFound'));
      } else if (statusCode === 403) {
        setError(t('salesAssessment.public.accessDenied'));
      } else if (statusCode === 410) {
        setError(t('salesAssessment.public.testExpired'));
        setCurrentStep('expired');
      } else {
        setError(err.response?.data?.errorMessage || t('salesAssessment.public.emailValidationError'));
      }
      setCurrentStep('error');
    } finally {
      setLoading(false);
    }
  };

  const handleStartTest = async () => {
    setLoading(true);
    try {
      const response = await api.put(
        `/sales-assessments-public/${testId}/start`,
        { applicantEmail }
      );

      if (response.data.errorCode === 0) {
        const data = response.data.response?.data;
        setTestData(data);

        // Calcular tiempo restante desde startedAt
        const startedSeconds = data.startedAt?._seconds ?? data.startedAt?.seconds ?? 0;
        const startedTime = startedSeconds * 1000;
        const elapsed = Math.floor((Date.now() - startedTime) / 1000);
        const remaining = (TEST_TIME_LIMIT * 60) - elapsed;

        setTimeRemaining(remaining > 0 ? remaining : 0);
        setCurrentStep('test');
      } else {
        setError(response.data.errorMessage || t('salesAssessment.public.startTestError'));
        setCurrentStep('error');
      }
    } catch (err) {
      console.error('Error starting test:', err);
      setError(err.response?.data?.errorMessage || t('salesAssessment.public.startTestError'));
      setCurrentStep('error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Cierre del test. `timeExpired` y `submitFailed` los manda el componente de
   * preguntas cuando se agotó el cronómetro, solo para decidir qué mensaje ve
   * el candidato: no se le puede asegurar que quedó registrado si el envío falló.
   */
  const handleCompleteTest = (result = {}) => {
    setCompletionInfo({
      timeExpired: Boolean(result.timeExpired),
      submitFailed: Boolean(result.submitFailed),
    });
    setCurrentStep('completed');
  };

  const renderEmailStep = () => (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <EmailIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            {t('salesAssessment.public.emailVerification')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('salesAssessment.public.emailVerificationDescription')}
          </Typography>
        </Box>

        <form onSubmit={handleAccessValidation}>
          <TextField
            fullWidth
            type="email"
            label={t('salesAssessment.public.enterEmailLabel')}
            value={applicantEmail}
            onChange={handleEmailChange}
            error={Boolean(emailError)}
            helperText={emailError}
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={loading || !applicantEmail.trim() || Boolean(emailError)}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {loading ? t('salesAssessment.public.verifying') : t('salesAssessment.public.accessTest')}
          </Button>
        </form>
      </Paper>
    </Container>
  );

  const renderInstructions = () => (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom align="center" color="primary">
          {t('salesAssessment.public.testTitle')}
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
          {t('salesAssessment.public.instructions')}
        </Typography>

        <List sx={{ mb: 4 }}>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary={t('salesAssessment.public.instruction1')} />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary={t('salesAssessment.public.instruction2')} />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary={t('salesAssessment.public.instruction3')} />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary={t('salesAssessment.public.instruction4')} />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary={t('salesAssessment.public.instruction5')} />
          </ListItem>
        </List>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
          <TimerIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="body1" color="text.secondary">
            {t('salesAssessment.public.timeRemaining')}: 20:00
          </Typography>
        </Box>

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleStartTest}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {loading ? t('salesAssessment.public.starting') : t('salesAssessment.public.startTest')}
          </Button>
        </Box>
      </Paper>
    </Container>
  );

  const renderTest = () => (
    <PublicSalesAssessmentQuestions
      testId={testId}
      testData={testData}
      timeRemaining={timeRemaining}
      onComplete={handleCompleteTest}
    />
  );

  const renderCompleted = () => (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
        {completionInfo.timeExpired ? (
          <>
            <Typography variant="h4" gutterBottom color="warning.main">
              {t('salesAssessment.public.timeUpTitle')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {completionInfo.submitFailed
                ? t('salesAssessment.public.timeUpSubmitFailedMessage')
                : t('salesAssessment.public.timeUpMessage')}
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="h4" gutterBottom color="success.main">
              {t('salesAssessment.public.testCompleted')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('salesAssessment.public.testCompletedDescription')}
            </Typography>
          </>
        )}
      </Paper>
    </Container>
  );

  const renderExpired = () => (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            {t('salesAssessment.public.testExpiredTitle')}
          </Typography>
          <Typography variant="body1">
            {t('salesAssessment.public.testExpiredMessage')}
          </Typography>
        </Alert>
      </Paper>
    </Container>
  );

  const renderError = () => (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            {t('salesAssessment.public.error')}
          </Typography>
          <Typography variant="body1">
            {error || t('salesAssessment.public.genericError')}
          </Typography>
        </Alert>
        <Button
          variant="outlined"
          fullWidth
          onClick={() => setCurrentStep('email')}
        >
          {t('salesAssessment.public.tryAgain')}
        </Button>
      </Paper>
    </Container>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
      {currentStep === 'email' && renderEmailStep()}
      {currentStep === 'instructions' && renderInstructions()}
      {currentStep === 'test' && renderTest()}
      {currentStep === 'completed' && renderCompleted()}
      {currentStep === 'expired' && renderExpired()}
      {currentStep === 'error' && renderError()}
    </Box>
  );
};

export default PublicSalesAssessment;